<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border">
    <h2 class="py-8 pl-8 font-semibold border-solid border-0 border-b border-gray-border">Create New Bot</h2>

    <form @submit.prevent="onSubmit" class="flex flex-col h-full justify-between pt-6">
      <div class="flex flex-col">
        <div class="px-8 pb-7">
          <base-label id="botName" label="Name"/>
          <base-input id="botName"
                      name="botName"
                      variant="editor"
                      type="string"
                      class="w-full border-gray py-px mt-1 h-10"
                      v-model.trim="botName"
          />
        </div>

        <div v-if="showUnreleased && selectedCreateFrom === CREATE_FROM_TYPES.TEMPLATE" class="px-8 pb-7">
          <base-label id="botWebsite" label="Website (optional)"/>
          <base-input id="botWebsite"
                      name="botWebsite"
                      variant="editor"
                      type="text"
                      placeholder="www.your-shop.com"
                      class="w-full border-gray py-px mt-1 h-10"
                      v-model.trim="website"
          />
          <p class="text-gray-dark text-sm mt-1">We'll read your brand colour, logo and tone to preset the chat theme and AI agent.</p>
        </div>

        <template>
          <settings-bot-market-form v-if="hasAvailableMarketOptions"
                                    v-model="marketForm"
                                    :multi-bots="multiBots"
                                    :existing-only="true"
                                    :allow-timezone-fallback="true"
                                    show-section-header
                                    section-header-class="pl-8"
                                    content-class="px-8 mt-2 pb-7"
                                    toggle-wrapper-class="mt-4"
          />
          <p v-else class="text-gray-dark px-8 mt-2 pb-7">
            No markets yet. <router-link :to="{ name: 'markets' }">Create one in Settings &gt; Markets</router-link> first.
          </p>
        </template>

        <div class="text-gray-dark border-solid border-0 border-t border-gray-border pt-6 px-8">
          <base-label id="createFromType" label="Create from"/>
          <div class="flex mt-2">
            <base-radio v-for="(createFromType, i) of createFromOptions"
                        :key="i"
                        :id="`createFromType${i}`"
                        :value="createFromType"
                        :name="'createFromType'"
                        :class="`ml-${i === 0 ? 0 : 10}`"
                        v-model="selectedCreateFrom"
            >
              {{ createFromType }}
            </base-radio>
          </div>
        </div>

        <!-- <div v-if="selectedCreateFrom === CREATE_FROM_TYPES.EMPTY" class="mt-4">
          <base-label id="createFromEmpty" label="Type"/>
          <base-select :value="botType"
                      :options="botTypeOptions"
                      class="w-full border-gray mt-1 h-10"
                      @input="botType = $event"
          />
        </div> -->
        <settings-bot-create-from-existing v-if="selectedCreateFrom === CREATE_FROM_TYPES.TEMPLATE"
                                           :create-from-type="CREATE_FROM_TYPES.TEMPLATE"
                                           :selected-option-id="selectedBotTemplateId"
                                           :options="botTemplates"
                                           :should-clone-variables="shouldCloneVariables"
                                           :is-prefix-from-input-disabled="true"
                                           :prefix-from="prefixFrom"
                                           :prefix-to="prefixTo"
                                           @on-checked="onChecked"
                                           @update-prefix-to="updatePrefixTo"
                                           @select-option="selectBotTemplate"
                                           class="px-8 mt-4"
        />
        <settings-bot-create-from-existing v-if="selectedCreateFrom === CREATE_FROM_TYPES.CLONE"
                                           :create-from-type="CREATE_FROM_TYPES.CLONE + ' from'"
                                           :selected-option-id="selectedBotId"
                                           :options="botList"
                                           :should-clone-variables="shouldCloneVariables"
                                           :prefix-from="prefixFrom"
                                           :prefix-to="prefixTo"
                                           @on-checked="onChecked"
                                           @update-prefix-from="updatePrefixFrom"
                                           @update-prefix-to="updatePrefixTo"
                                           @select-option="selectBotClone"
                                           class="px-8 mt-4"
        />
      </div>

      <busy-button id="createButton"
                   :variant="createButtonVariant"
                   class="w-1/2 mb-10 ml-8"
                   :is-loading="isLoading"
                   :is-disabled="isCreateDisabled || isLoading"
      >
        <span class="w-full">{{ createButtonText }}</span>
      </busy-button>

    </form>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import SettingsBotCreateFromExisting from '@/components/settings/bots/SettingsBotCreateFromExisting.vue'
import SettingsBotMarketForm from '@/components/settings/bots/SettingsBotMarketForm.vue'
import { BOT_TYPES } from '@/utils/bot.constants'
import { SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { CREATE_BUTTON_LABELS } from '@/utils/text.constants'
import {
  createDefaultMarketForm,
  getMarketFormLanguageTimezone,
  isMarketFormValid,
  resolveMarketId
} from '@/utils/market.utils'
import botModule from '@/store/modules/bot.module'
import marketModule from '@/store/modules/market.module'
import botService from '@/services/amio/bot.service'
import multiBotService from '@/services/amio/multi-bot.service'
import botTemplatesService from '@/services/amio/bot-templates.service'
import toaster from '@/utils/toaster'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import previewModule from '@/store/modules/preview.module'

const CREATE_FROM_TYPES = {
  // EMPTY: 'Empty',
  TEMPLATE: 'Template',
  CLONE: 'Clone'
}

export default {
  name: 'SettingsBotCreate',
  components: { BusyButton, SettingsBotCreateFromExisting, SettingsBotMarketForm },

  props: {
    multiBots: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      botType: BOT_TYPES.CHAT,
      botName: '',
      website: '',
      botLanguage: '',
      botTimezone: 'Europe/Prague',
      botTemplates: [],
      marketForm: createDefaultMarketForm(),
      isLoading: false,
      selectedCreateFrom: CREATE_FROM_TYPES.TEMPLATE,
      selectedBotTemplateId: '',
      selectedBotId: '',
      shouldCloneVariables: false,
      prefixFrom: '',
      prefixTo: '',
      CREATE_FROM_TYPES,
      createButtonVariant: SAVE_BUTTON_VARIANTS.DEFAULT,
      createButtonText: CREATE_BUTTON_LABELS.DEFAULT
    }
  },
  computed: {
    botList: botModule.getBotList,
    error: previewModule.error,

    hasAvailableMarketOptions() {
      return marketModule.getMarketList().length > 0
    },

    createFromOptions() {
      const hideClone = isEmpty(this.botList) || this.showUnreleased
      return Object.values(CREATE_FROM_TYPES).filter(type => !hideClone || type !== CREATE_FROM_TYPES.CLONE)
    },
    botTypeOptions() {
      return Object.values(BOT_TYPES).map((botType) => ({
        text: botType[0].toUpperCase() + botType.slice(1),
        value: botType
      }))
    },
    shouldTranslateBot() {
      if (!this.botLanguage) return false
      if (this.botTemplateLanguage) return this.botTemplateLanguage !== this.botLanguage
      if (this.botCloneLanguage) return this.botCloneLanguage !== this.botLanguage

      return false
    },
    botTemplateLanguage() {
      return this.botTemplates.find(t => t.id === this.selectedBotTemplateId)?.botLanguage
    },
    botCloneLanguage() {
      return this.botList.find(b => b.id === this.selectedBotId)?.language
    },
    isMarketFormValid() {
      return isMarketFormValid(this.marketForm, this.hasAvailableMarketOptions, this.botList)
    },
    isCreateDisabled() {
      if (this.showUnreleased && (!this.hasAvailableMarketOptions || !this.isMarketFormValid)) return true
      return !this.botName
    }
  },

  watch: {
    marketForm: {
      deep: true,
      handler(form) {
        const { language, timezone } = getMarketFormLanguageTimezone(form)
        if (language) this.botLanguage = language
        if (timezone) this.botTimezone = timezone
      }
    },

    createFromOptions(options) {
      if (!options.includes(this.selectedCreateFrom)) {
        this.selectedCreateFrom = CREATE_FROM_TYPES.TEMPLATE
      }
    }
  },

  methods: {
    selectBotTemplate(botTemplateId) {
      const botTemplate = this.botTemplates.find((template) => template.id === botTemplateId)
      this.selectedBotTemplateId = botTemplate.id
      this.prefixFrom = botTemplate.variablesPrefix
      this.botType = botTemplate.botType
      if (!this.botLanguage) {
        this.botLanguage = botTemplate.botLanguage
        this.marketForm = {
          ...this.marketForm,
          language: botTemplate.botLanguage
        }
      }
    },
    selectBotClone(botId) {
      const bot = this.botList.find((bot) => bot.id === botId)
      if (this.selectedBotTemplateId && !this.selectedBotId) this.prefixFrom = ''
      this.selectedBotId = bot.id
      this.botType = bot.type
      if (!this.botLanguage) this.botLanguage = bot.language
    },
    onChecked(shouldCloneVariables) {
      if (shouldCloneVariables) this.prefixTo = this.botLanguage + '-'
      this.shouldCloneVariables = shouldCloneVariables
    },
    updatePrefixFrom(prefixFrom) {
      this.prefixFrom = prefixFrom
    },
    updatePrefixTo(prefixTo) {
      this.prefixTo = prefixTo
    },
    resolveTranslateParams() {
      return this.shouldTranslateBot ? {
        from_language: this.selectedCreateFrom === CREATE_FROM_TYPES.TEMPLATE ? this.botTemplateLanguage : this.botCloneLanguage,
        to_language: this.botLanguage
      } : null
    },
    resolveCloneVariablesParams() {
      return this.shouldCloneVariables ? {
        prefix_original: this.prefixFrom,
        prefix_converted: this.prefixTo
      } : null
    },
    onSubmit() {
      if (this.isCreateDisabled) return
      this.createBot()
    },

    async createBot() {
      this.isLoading = true
      try {
        const translateParams = this.resolveTranslateParams()
        const cloneVariablesParams = this.resolveCloneVariablesParams()
        const { marketId, marketName, timezone, language } = await resolveMarketId(this.marketForm)
        if (this.selectedCreateFrom === CREATE_FROM_TYPES.TEMPLATE) {
          const newBotFromTemplate = await multiBotService.createFromTemplate(
            this.botName,
            this.selectedBotTemplateId,
            marketName,
            timezone,
            this.botType,
            language,
            marketId,
            this.website
          )
          const newBot = newBotFromTemplate.primaryBot
          newBot.is_primary_variant = true
          this.$emit('process-new-bot', newBot)
        } else if (this.selectedCreateFrom === CREATE_FROM_TYPES.CLONE) {
          const newBotFromClone = await botService.createFromClone(this.botName, this.botTimezone, this.botLanguage, this.selectedBotId, marketId, marketName, translateParams, cloneVariablesParams)
          newBotFromClone.is_primary_variant = true
          this.$emit('process-new-bot', newBotFromClone)
        }
        toaster.info('Creating a new bot, this might take up to 1 minute.')
      } catch (err) {
        const data = err.response?.data
        const firstError = head(data?.details.errors)
        const errorMessage = firstError?.message || err.message

        if (errorMessage.startsWith('These variables are already defined')) {
          const callToAction = '<br/>Either change the "New variables prefix" or <a href="/settings/variables" target="_blank">delete the above variables.</a>'
          toaster.error(errorMessage, err, {}, null, callToAction)
        } else if(errorMessage === 'Prefixes cannot be the same' || errorMessage.startsWith('Variables')) {
          this.error = { description: errorMessage }
        } else if(errorMessage.startsWith('Maximum workspaces')) {
          toaster.error('Failed to create new bot, please contact ', err, {}, null, '<a href="mailto:support@amio.io?subject=Error%3A%20Create%20New%20Bot">support@amio.io</a>')
        } else if(data?.code === 'generic' && errorMessage.includes('prefix')) {
          this.error = firstError
        } else {
          toaster.error(errorMessage)
        }
      } finally {
        this.isLoading = false
        this.startButtonStateTimeout()
      }
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 8000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.createButtonVariant = SAVE_BUTTON_VARIANTS[buttonState]
      this.createButtonText = CREATE_BUTTON_LABELS[buttonState]
    }
  },
  async mounted() {
    this.botTemplates = await botTemplatesService.list()
    this.error = null
  }
}
</script>
